import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, Ticket, Power } from "lucide-react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loading } from "@/components/common/Loading";
import { AdminRefreshButton } from "@/components/admin/AdminRefreshButton";

const PLANS = [
  { key: "pro", label: "PRO" },
  { key: "premium", label: "CAPITAL" },
];
const INTERVALS = [
  { key: "month", label: "Monthly" },
  { key: "year", label: "Annual" },
];

const EMPTY = {
  code: "",
  discount_type: "percent",
  value: "",
  duration: "once",
  duration_in_months: 3,
  tiers: [],
  intervals: [],
  max_redemptions: "",
  expires_at: "",
};

function Toggle({ active, onClick, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`rounded-full border px-3 py-1 text-xs font-[560] transition-colors ${
        active
          ? "border-primary bg-primary/10 text-primary"
          : "border-border bg-background text-muted-foreground hover:bg-secondary"
      }`}
    >
      {children}
    </button>
  );
}

function discountText(c) {
  return c.discount_type === "percent" ? `${c.value}% off` : `$${c.value} off`;
}
function durationText(c) {
  if (c.duration === "forever") return "Forever";
  if (c.duration === "repeating") return `${c.duration_in_months} mo`;
  return "First payment";
}
function scopeText(c) {
  const plans = (c.tiers || []).length >= 2 ? "All plans" : (c.plan_labels || c.tiers || []).join(", ");
  const ivs = (c.intervals || []).length >= 2
    ? "Monthly + Annual"
    : (c.intervals || []).map((i) => (i === "year" ? "Annual" : "Monthly")).join(", ");
  return `${plans} · ${ivs}`;
}

export default function AdminCoupons() {
  const [coupons, setCoupons] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/coupons");
      setCoupons(data.coupons || []);
    } catch (e) {
      setCoupons((p) => p || []);
      toast.error(e?.response?.data?.detail || "Could not load coupons.");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const toggleIn = (field, key) =>
    setForm((f) => ({
      ...f,
      [field]: f[field].includes(key) ? f[field].filter((k) => k !== key) : [...f[field], key],
    }));

  const create = async () => {
    if (!form.code.trim()) return toast.error("Enter a promo code.");
    if (!Number(form.value)) return toast.error("Enter a discount value.");
    setCreating(true);
    try {
      const payload = {
        code: form.code.trim().toUpperCase(),
        discount_type: form.discount_type,
        value: Number(form.value),
        duration: form.duration,
        duration_in_months: form.duration === "repeating" ? Number(form.duration_in_months) : null,
        tiers: form.tiers,
        intervals: form.intervals,
        max_redemptions: form.max_redemptions ? Number(form.max_redemptions) : null,
        expires_at: form.expires_at || null,
      };
      await api.post("/admin/coupons", payload);
      toast.success(`Coupon ${payload.code} created.`);
      setForm(EMPTY);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create coupon.");
    } finally {
      setCreating(false);
    }
  };

  const deactivate = async (id) => {
    try {
      await api.post(`/admin/coupons/${id}/deactivate`);
      toast.success("Coupon deactivated.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not deactivate.");
    }
  };

  if (coupons === null) return <Loading />;

  return (
    <div data-testid="admin-coupons">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-[680] text-foreground">Coupons &amp; discount codes</h2>
          <p className="text-sm text-muted-foreground">
            Create a code customers enter at checkout (the &ldquo;Add promotion code&rdquo; field on the Stripe payment page). Restrict by plan, billing interval, and how long the discount lasts.
          </p>
        </div>
        <AdminRefreshButton testId="admin-refresh-coupons" onRefresh={load} />
      </div>

      {/* Create form */}
      <Card className="mt-5 p-5" data-testid="coupon-create-card">
        <h3 className="flex items-center gap-2 text-base font-[680] text-foreground">
          <Ticket className="h-4 w-4 text-muted-foreground" /> New coupon
        </h3>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <div>
            <Label htmlFor="coupon-code">Promo code</Label>
            <Input
              id="coupon-code"
              value={form.code}
              onChange={(e) => set({ code: e.target.value.toUpperCase() })}
              placeholder="LAUNCH20"
              className="mt-1.5 font-mono uppercase tracking-wider"
              data-testid="coupon-code"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Discount</Label>
              <Select value={form.discount_type} onValueChange={(v) => set({ discount_type: v })}>
                <SelectTrigger className="mt-1.5" data-testid="coupon-discount-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="percent">% off</SelectItem>
                  <SelectItem value="amount">$ off</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="coupon-value">{form.discount_type === "percent" ? "Percent" : "Amount ($)"}</Label>
              <Input
                id="coupon-value"
                type="number"
                value={form.value}
                onChange={(e) => set({ value: e.target.value })}
                placeholder={form.discount_type === "percent" ? "20" : "5.00"}
                className="mt-1.5"
                data-testid="coupon-value"
              />
            </div>
          </div>

          <div>
            <Label>Duration</Label>
            <Select value={form.duration} onValueChange={(v) => set({ duration: v })}>
              <SelectTrigger className="mt-1.5" data-testid="coupon-duration"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="once">First payment only</SelectItem>
                <SelectItem value="repeating">Repeating (months)</SelectItem>
                <SelectItem value="forever">Forever</SelectItem>
              </SelectContent>
            </Select>
            {form.duration === "repeating" && (
              <div className="mt-2">
                <Input
                  type="number"
                  value={form.duration_in_months}
                  onChange={(e) => set({ duration_in_months: e.target.value })}
                  placeholder="3"
                  data-testid="coupon-duration-months"
                />
                <p className="mt-1 text-[11px] text-muted-foreground">
                  Months the discount applies. Monthly plan: N months = N billing cycles. Annual plan: use 12 per year.
                </p>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label htmlFor="coupon-max">Max redemptions</Label>
              <Input id="coupon-max" type="number" value={form.max_redemptions} onChange={(e) => set({ max_redemptions: e.target.value })} placeholder="Unlimited" className="mt-1.5" data-testid="coupon-max" />
            </div>
            <div>
              <Label htmlFor="coupon-expires">Expires</Label>
              <Input id="coupon-expires" type="date" value={form.expires_at} onChange={(e) => set({ expires_at: e.target.value })} className="mt-1.5" data-testid="coupon-expires" />
            </div>
          </div>
        </div>

        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <div>
            <Label className="text-xs text-muted-foreground">Applies to plans <span className="font-normal">(none = all paid)</span></Label>
            <div className="mt-1.5 flex flex-wrap gap-2">
              {PLANS.map((p) => (
                <Toggle key={p.key} active={form.tiers.includes(p.key)} onClick={() => toggleIn("tiers", p.key)} testId={`coupon-plan-${p.key}`}>{p.label}</Toggle>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Applies to billing <span className="font-normal">(none = both)</span></Label>
            <div className="mt-1.5 flex flex-wrap gap-2">
              {INTERVALS.map((iv) => (
                <Toggle key={iv.key} active={form.intervals.includes(iv.key)} onClick={() => toggleIn("intervals", iv.key)} testId={`coupon-interval-${iv.key}`}>{iv.label}</Toggle>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-5">
          <Button onClick={create} disabled={creating} data-testid="coupon-create-button">
            {creating ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Plus className="mr-1.5 h-4 w-4" />} Create coupon
          </Button>
        </div>
      </Card>

      {/* List */}
      <div className="mt-6 space-y-2" data-testid="coupon-list">
        {coupons.map((c) => (
          <Card key={c.id} className={`flex flex-wrap items-center justify-between gap-3 p-4 ${c.active ? "" : "opacity-60"}`} data-testid={`coupon-${c.id}`}>
            <div className="flex items-center gap-3">
              <code className="rounded-md border border-border bg-secondary/60 px-2.5 py-1 font-mono text-sm font-[600] tracking-wider text-foreground">{c.code}</code>
              <div className="text-sm">
                <div className="font-[600] text-foreground">{discountText(c)} <span className="text-muted-foreground">· {durationText(c)}</span></div>
                <div className="text-xs text-muted-foreground">{scopeText(c)}</div>
              </div>
            </div>
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              <span data-testid={`coupon-redeemed-${c.id}`}>{c.times_redeemed || 0}{c.max_redemptions ? `/${c.max_redemptions}` : ""} used</span>
              {c.expires_at && <span>exp {c.expires_at}</span>}
              {c.active ? (
                <Badge className="bg-teal-50 text-teal-700 hover:bg-teal-50">Active</Badge>
              ) : (
                <Badge variant="secondary">Inactive</Badge>
              )}
              {c.active && (
                <Button size="sm" variant="ghost" className="text-destructive" onClick={() => deactivate(c.id)} data-testid={`coupon-deactivate-${c.id}`}>
                  <Power className="mr-1 h-3.5 w-3.5" /> Deactivate
                </Button>
              )}
            </div>
          </Card>
        ))}
        {coupons.length === 0 && <p className="text-sm text-muted-foreground">No coupons yet. Create one above.</p>}
      </div>
    </div>
  );
}
